/**
 * Smart Variant Selector
 *
 * Every option click ends on a real variant, and the main image shows it.
 *
 * Products have three options (Size, Canvas Type, Frame Color) and not every
 * combination exists: a stretched canvas has no frame, Brown comes only as a
 * classic frame, Natural only as a float frame, and some sizes exist in one
 * format only. When a click makes a combination that doesn't exist, the other
 * options move to the closest variant that does. The clicked value always
 * stays; after that the format is kept if possible, then the frame colour,
 * then the size (or the nearest size).
 *
 * How: a capture-phase change listener on <sht-variant-radios> runs before the
 * theme's own handler, so the theme only ever sees a valid selection (one
 * section fetch, no "Unavailable" flash). The variant list is the JSON the
 * theme embeds in the picker, not the greyed-out option classes.
 *
 * Image: the gallery shows only the selected variant's photo plus the general
 * photos, so the theme cannot find a newly selected variant's photo until its
 * section fetch returns (and on phones it then throws reading offsetLeft,
 * which also stops it greying out unavailable options). Before the theme
 * looks, the first gallery slide is switched to the new variant's photo.
 *
 * Usage: loaded from theme.liquid before </body> (defer, after theme.js).
 */
(function () {
  'use strict';

  const PATTERNS = {
    canvasType: /canvas\s*type/i,
    frameColor: /(frame|framing)\s*colou?r/i,
    size: /size/i
  };
  // When the colour has to change, prefer these (Black first)
  const COLOUR_PREFERENCE = ['Black', 'Brown', 'Natural', 'White', 'Gold', 'Silver'];

  // Outside English the picker shows translated option names and values. The shared block in theme.liquid
  // (data-gr-i18n="options") gives them; they are mapped back to English once, for PATTERNS and COLOUR_PREFERENCE.
  const EN = {
    options: { size: 'Size', canvas_type: 'Canvas Type', frame_color: 'Frame Color' },
    values: { stretched_canvas: 'Stretched Canvas', framed_canvas: 'Framed Canvas', float_frame: 'Float Frame',
              black: 'Black', brown: 'Brown', natural: 'Natural', white: 'White', no_frame: 'No Frame' }
  };
  const GR_OPT = (() => {
    const d = { options: Object.assign({}, EN.options), values: Object.assign({}, EN.values) };
    try {
      const el = document.querySelector('[data-gr-i18n="options"]');
      const j = el && JSON.parse(el.textContent);
      return j ? { options: Object.assign(d.options, j.options), values: Object.assign(d.values, j.values) } : d;
    } catch (e) {
      return d;
    }
  })();
  /** Shopify HTML-escapes a translation whose key doesn't end in _html; the picker's names and values are plain text. */
  function unescapeHtml(s) {
    return String(s).replace(/&(?:(amp|lt|gt|quot|apos)|#(\d+)|#x([0-9a-f]+));/gi, (m, name, dec, hex) => {
      if (name) return { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }[name.toLowerCase()];
      const code = dec ? Number(dec) : parseInt(hex, 16);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : m;
    });
  }
  function englishMap(group) {
    const map = Object.create(null);
    Object.keys(EN[group]).forEach((key) => { map[unescapeHtml(GR_OPT[group][key])] = EN[group][key]; });
    return map;
  }
  const NAME_EN = englishMap('options');
  const VALUE_EN = englishMap('values');
  /** An option name or value in English; anything else unchanged. */
  const english = (map, s) => (map[s] !== undefined ? map[s] : s);
  const IMAGE_WIDTHS = [320, 450, 550, 750, 950, 1024, 1200, 1400, 1600, 1800, 2000, 2400];
  // Keeping an option matters in this order; one change of a heavier option outweighs any number of lighter ones
  const COST = { canvasType: 1e6, frameColor: 1e3, size: 1 };

  /** "24×30"" -> [24, 30] */
  function dims(value) {
    const m = /(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/.exec(String(value));
    return m ? [Number(m[1]), Number(m[2])] : null;
  }

  function sizeDistance(a, b) {
    const da = dims(a);
    const db = dims(b);
    if (!da || !db) return 500;
    return Math.abs(da[0] - db[0]) + Math.abs(da[1] - db[1]);
  }

  function withWidth(src, width) {
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  class SmartVariantSelector {
    constructor(picker) {
      this.picker = picker;
      const json = picker.querySelector('script[type="application/json"]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.containers = Array.from(picker.querySelectorAll('.js-variant-radio-container'));
      this.names = this.containers.map((c) => c.querySelector('.js-variant-radio-item')?.getAttribute('name') || '');
      this.roles = this.names.map((local) => english(NAME_EN, local)).map((name) =>
        PATTERNS.canvasType.test(name) ? 'canvasType' : PATTERNS.frameColor.test(name) ? 'frameColor' : PATTERNS.size.test(name) ? 'size' : 'other'
      );
      // Values in the order the picker lists them (a stable tie-break)
      this.valueOrder = this.containers.map((c) => Array.from(c.querySelectorAll('.js-variant-radio-item')).map((i) => i.value));
      this.last = this.checked();
      if (!this.variants.length || this.roles.indexOf('size') === -1) return;
      picker.addEventListener('change', (event) => this.onChange(event), true);
    }

    checked() {
      return this.containers.map((c) => c.querySelector('.js-variant-radio-item:checked')?.value);
    }

    find(values) {
      return this.variants.find((v) => v.options.every((o, i) => o === values[i]));
    }

    onChange(event) {
      const input = event.target;
      if (!input || !input.classList || !input.classList.contains('js-variant-radio-item')) return;
      const position = this.names.indexOf(input.getAttribute('name'));
      if (position === -1) return;
      const now = this.checked();
      let variant = this.find(now);
      if (!variant) {
        variant = this.closest(position, input.value, this.last);
        if (variant) this.select(variant.options);
      }
      if (!variant) return;
      this.last = variant.options.slice();
      this.showImage(variant);
    }

    /** The real variant with the clicked value that changes the least of the previous selection. */
    closest(position, value, previous) {
      let candidates = this.variants.filter((v) => v.options[position] === value);
      const available = candidates.filter((v) => v.available);
      if (available.length) candidates = available;
      let best = null;
      let bestScore = Infinity;
      candidates.forEach((v) => {
        let score = 0;
        v.options.forEach((option, i) => {
          if (i === position || option === previous[i]) return;
          const role = this.roles[i];
          if (role === 'size') {
            score += COST.size * (1 + sizeDistance(previous[i], option));
          } else if (role === 'frameColor') {
            const rank = COLOUR_PREFERENCE.indexOf(english(VALUE_EN, option));
            score += COST.frameColor * (1 + (rank === -1 ? COLOUR_PREFERENCE.length : rank) / 100);
          } else {
            score += (COST[role] || COST.canvasType) * (1 + this.valueOrder[i].indexOf(option) / 100);
          }
        });
        if (score < bestScore) {
          bestScore = score;
          best = v;
        }
      });
      return best;
    }

    /** Check the variant's values without firing events: the theme's own handler reads them next. */
    select(options) {
      options.forEach((value, i) => {
        const input = Array.from(this.containers[i].querySelectorAll('.js-variant-radio-item')).find((r) => r.value === value);
        if (input && !input.checked) input.checked = true;
      });
    }

    /** Put the variant's photo in the first gallery slide, where the theme then finds and shows it. */
    showImage(variant) {
      const media = variant.featured_media;
      const src = media && media.preview_image && media.preview_image.src;
      if (!src) return;
      const section = this.picker.dataset.section;
      const slideshow = document.querySelector('sht-prd-slideshow[data-section="' + section + '"]');
      if (!slideshow) return;
      const wanted = section + '-' + media.id;
      if (slideshow.querySelector('sht-carousel-itm[data-media-id="' + wanted + '"]')) return;
      const slide = slideshow.querySelector('sht-carousel-itm[data-media-id]');
      const img = slide && slide.querySelector('img');
      if (!slide || !img) return;
      slide.setAttribute('data-media-id', wanted);
      img.srcset = IMAGE_WIDTHS.map((w) => withWidth(src, w) + ' ' + w + 'w').join(', ');
      img.src = withWidth(src, 1200);
      if (media.alt) img.alt = media.alt;
    }
  }

  const initialised = new WeakSet();

  function initSmartVariantSelectors(root) {
    (root || document).querySelectorAll('sht-variant-radios').forEach((picker) => {
      if (initialised.has(picker)) return;
      initialised.add(picker);
      new SmartVariantSelector(picker);
    });
  }

  // Quick view and other content added later
  function watchForDynamicContent() {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.tagName === 'SHT-VARIANT-RADIOS' || (node.querySelector && node.querySelector('sht-variant-radios'))) {
            initSmartVariantSelectors(node.parentElement || node);
          }
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initSmartVariantSelectors();
      watchForDynamicContent();
    });
  } else {
    initSmartVariantSelectors();
    watchForDynamicContent();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => initSmartVariantSelectors(event.target));
  }

  window.SmartVariantSelector = { init: initSmartVariantSelectors };
})();
